/**
 * @license
 * Copyright 2018 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as tfc from '@tensorflow/tfjs-core';
import { getParamValue } from './utils';
export const executeOp = async (node, tensorMap, context) => {
    switch (node.op) {
        case 'NonMaxSuppressionV5':
        case 'NonMaxSuppressionV4':
        case 'NonMaxSuppressionV3':
        case 'NonMaxSuppressionV2': {
            const boxes = getParamValue('boxes', node, tensorMap, context);
            const scores = getParamValue('scores', node, tensorMap, context);
            const maxOutputSize = getParamValue('maxOutputSize', node, tensorMap, context);
            const iouThreshold = getParamValue('iouThreshold', node, tensorMap, context);
            const scoreThreshold = getParamValue('scoreThreshold', node, tensorMap, context);
            if (node.op === 'NonMaxSuppressionV5') {
                const softNmsSigma = getParamValue('softNmsSigma', node, tensorMap, context);
                const result = await tfc.image.nonMaxSuppressionWithScoreAsync(boxes, scores, maxOutputSize, iouThreshold, scoreThreshold, softNmsSigma);
                return [result.selectedIndices, result.selectedScores];
            }
            if (node.op === 'NonMaxSuppressionV4') {
                const padToMaxOutputSize = getParamValue('padToMaxOutputSize', node, tensorMap, context);
                const result = await tfc.image.nonMaxSuppressionPaddedAsync(boxes, scores, maxOutputSize, iouThreshold, scoreThreshold, padToMaxOutputSize);
                return [result.selectedIndices, result.validOutputs];
            }
            return [await tfc.image.nonMaxSuppressionAsync(boxes, scores, maxOutputSize, iouThreshold, scoreThreshold)];
        }
        case 'Where': {
            const condition = getParamValue('condition', node, tensorMap, context)
                .asType('bool');
            const result = [await tfc.whereAsync(condition)];
            condition.dispose();
            return result;
        }
        case 'ListDiff': {
            return tfc.setdiff1dAsync(getParamValue('x', node, tensorMap, context), getParamValue('y', node, tensorMap, context));
        }
        default:
            throw TypeError(`Node type ${node.op} is not implemented`);
    }
};
export const CATEGORY = 'dynamic';
//# sourceMappingURL=dynamic_executor.js.map